/**
 * Pure helpers for the Kory tool-call action cards. Kept free of React so
 * unit tests can cover label extraction, diff building, and token formatting
 * without a DOM.
 */
import { differenceInMilliseconds } from "date-fns";
import type { ToolCallStatus, ToolContentBlock } from "@/contexts/kory-context";

/**
 * Elapsed ms between a tool's start ISO timestamp and an end (ISO string,
 * epoch ms, or Date). Returns null when either side is missing/unparseable.
 * Uses date-fns so we don't invent raw millisecond math.
 */
export function toolCallDurationMs(
  startedAt: string | null | undefined,
  endedAt: string | number | Date | null | undefined,
): number | null {
  if (!startedAt || endedAt == null || endedAt === "") return null;
  const start = new Date(startedAt);
  const end = typeof endedAt === "number" ? new Date(endedAt) : new Date(endedAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.max(0, differenceInMilliseconds(end, start));
}

/**
 * Terminal-state helper for still-running tool rows (done / error / cancel /
 * history close sweeps). Flips status and stamps durationMs from startedAt so
 * action cards keep a measurable elapsed even when no tool_result arrived.
 */
export function settleRunningToolCall(
  tc: ToolCallStatus,
  status: ToolCallStatus["status"],
  endedAt: string | number | Date = Date.now(),
): ToolCallStatus {
  if (tc.status !== "running" || status === "running") return tc;
  return {
    ...tc,
    status,
    durationMs: toolCallDurationMs(tc.startedAt, endedAt) ?? tc.durationMs,
  };
}

// Human labels for known MCP tools (personal agent). Unknown tools render
// their raw name as the verb.
export const TOOL_LABELS: Record<string, string> = {
  list_tables: "Listing tables",
  describe_table: "Describing table",
  run_query: "Running query",
  execute_sql: "Query",
  list_sops: "Searching SOPs",
  get_sop: "Reading SOP",
  get_playwrighter_job: "Checking job status",
  close_invoice: "Closing invoice",
  send_invoice_email: "Sending invoice email",
  collect_and_apply_payment: "Collecting payment",
  unapply_duplicate_payment: "Unapplying duplicate payment",
  booking_a_job: "Booking a job",
  draft_call_script: "Drafted call script",
  draft_text_message: "Drafted SMS",
  draft_proposal_skeleton: "Drafted proposal skeleton",
  queue_advance_stage: "Queued stage advance",
  queue_close_lost: "Queued close-lost",
  queue_schedule_activity: "Queued activity",
  complete_todo: "Marked TODO complete",
  write_artifact: "Publishing artifact",
  list_artifacts: "Listing artifacts",
  explain_cell: "Explaining this value",
  marketing_website_find: "Finding Marketing Websites",
  marketing_website_get: "Reading the Marketing Website",
  marketing_website_edit: "Editing the Marketing Website",
  marketing_website_undo: "Undoing a Marketing Website change",
  marketing_website_preview: "Previewing the Marketing Website",
  marketing_website_rebuild: "Rebuilding the Marketing Website",
  tech_spend_preview_proposal: "Drafting Technology Spend proposal",
  tech_spend_get_proposal: "Loading Technology Spend proposal",
  tech_spend_clarify_proposal: "Answering Technology Spend clarification",
  tech_spend_edit_proposal: "Editing Technology Spend proposal",
  tech_spend_cancel_proposal: "Cancelling Technology Spend proposal",
  tech_spend_apply_proposal: "Applying Technology Spend proposal",
  tech_spend_undo_proposal: "Undoing Technology Spend proposal",
};

export type ModelFamily = "opus" | "sonnet" | "haiku" | "fable" | "gpt" | "other";

/** Strip agent/ prefix, [1m] suffix, and leading claude- for display. */
export function bareModelId(id: string): string {
  return id
    .replace(/^[^/]+\//, "")
    .replace(/\[1m\]$/i, "")
    .replace(/^claude-/, "");
}

/** Family for the colored chip dot. */
export function modelFamily(id: string): ModelFamily {
  const rest = bareModelId(id).toLowerCase();
  if (rest.includes("fable")) return "fable";
  if (rest.includes("opus")) return "opus";
  if (rest.includes("sonnet")) return "sonnet";
  if (rest.includes("haiku")) return "haiku";
  if (rest.startsWith("gpt") || rest.includes("codex") || rest.includes("sol")) return "gpt";
  return "other";
}

/**
 * Short chip label matching the mockup: "Haiku 4.5", "Sonnet 5", "Opus 4.8",
 * "Fable 5", "GPT 5.5". Falls back to the bare id.
 */
export function modelChipLabel(id: string): string {
  const rest = bareModelId(id);
  const gpt = /^gpt-?(\d+(?:\.\d+)?)(?:-(.+))?$/i.exec(rest);
  if (gpt) {
    const ver = gpt[1];
    const suffix = gpt[2] ? `-${gpt[2]}` : "";
    return `GPT ${ver}${suffix}`;
  }
  // haiku-4-5 / opus-4-8 / sonnet-5 / fable-5 → "Haiku 4.5" / "Opus 4.8" / …
  const fam =
    /^(fable|opus|sonnet|haiku|sol)(?:[-_]?(\d+(?:[-.]\d+)*))?/i.exec(rest);
  if (fam) {
    const name = fam[1][0].toUpperCase() + fam[1].slice(1).toLowerCase();
    if (fam[2]) {
      const ver = fam[2].replace(/-/g, ".");
      return `${name} ${ver}`;
    }
    return name;
  }
  return rest;
}

/** Compact `in → out` token string; k-suffix above 999. */
export function formatTokens(tokens: { input: number; output: number } | null | undefined): string | null {
  if (!tokens) return null;
  return `${compactTok(tokens.input)} → ${compactTok(tokens.output)}`;
}

function compactTok(n: number): string {
  if (!Number.isFinite(n)) return "0";
  // Keep tiny counts as raw integers; everything else is k-suffixed so the
  // rail stays short (matches the mockup's `0.4k → 0.1k` style).
  if (Math.abs(n) < 100) return String(Math.round(n));
  const k = n / 1000;
  return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")}k`;
}

/** Duration rail label: `0.3s`, `1.9s`, `12s`, `1.2m`. */
export function formatDuration(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  if (ms < 1000) return `${(ms / 1000).toFixed(1)}s`.replace(/^0\.0s$/, "0s");
  if (ms < 60_000) {
    const s = ms / 1000;
    return s < 10 ? `${s.toFixed(1)}s` : `${Math.round(s)}s`;
  }
  const m = ms / 60_000;
  return m < 10 ? `${m.toFixed(1)}m` : `${Math.round(m)}m`;
}

export type CardKind =
  | "terminal"
  | "edit"
  | "read"
  | "search"
  | "sql"
  | "router"
  | "classify"
  | "generic";

/** Map ACP kind + tool name onto a specialized card body. */
export function resolveCardKind(toolCall: ToolCallStatus): CardKind {
  const bare = bareToolName(toolCall.tool);
  // Synthetic pre-classifier rows (U1–U2 classify tool card).
  if (bare === "classify" || toolCall.kind === "classify") return "classify";
  // Synthetic router-acp decision rows (materialized from disclosure lines).
  if (bare === "router" || toolCall.kind === "router") return "router";
  if (bare === "execute_sql" || bare === "run_query") return "sql";
  if (toolCall.kind === "execute") return "terminal";
  if (toolCall.kind === "edit") return "edit";
  if (toolCall.kind === "read") return "read";
  if (toolCall.kind === "search") return "search";
  // Pre-kind / Grok-style tools with no ACP kind — infer from name + input.
  if (toolCall.kind == null) {
    if (bare === "run_terminal_command" || bare === "Bash" || bare === "Terminal") {
      return "terminal";
    }
    if (
      bare === "search_replace" ||
      bare === "write" ||
      bare === "Write" ||
      bare === "Edit"
    ) {
      return "edit";
    }
    if (bare === "read_file" || bare === "Read" || bare.startsWith("Read ")) {
      return "read";
    }
    if (bare === "grep" || bare === "search_tool" || bare === "rg") return "search";
    if (typeof toolCall.input?.command === "string") return "terminal";
    if (typeof toolCall.input?.old_string === "string") return "edit";
    if (typeof toolCall.input?.pattern === "string") return "search";
    if (
      typeof toolCall.input?.target_file === "string" ||
      typeof toolCall.input?.file_path === "string"
    ) {
      return "read";
    }
  }
  return "generic";
}

export function bareToolName(tool: string): string {
  if (tool.startsWith("mcp__")) {
    const parts = tool.split("__");
    return parts.length >= 3 ? parts.slice(2).join("__") : tool;
  }
  return tool;
}

export interface CardLabel {
  verb: string;
  arg: string;
  failNote?: string;
}

/** Collapsed-row verb + argument for the header. */
export function cardLabel(toolCall: ToolCallStatus): CardLabel {
  const kind = resolveCardKind(toolCall);
  const input = toolCall.input ?? {};
  const bare = bareToolName(toolCall.tool);

  if (kind === "router") {
    // "router: Opus" / "router: Fable 5" — company icon sits beside the verb.
    const model = toolCall.model ?? (typeof input.model === "string" ? input.model : null);
    const effort =
      typeof input.effort === "string" && input.effort && input.effort !== "auto"
        ? ` (${input.effort})`
        : "";
    const arg = model ? `${modelChipLabel(model)}${effort}` : "auto";
    return { verb: "router:", arg };
  }
  if (kind === "classify") {
    const modes = Array.isArray(input.modes)
      ? (input.modes as string[]).join(", ")
      : "none";
    return { verb: "classify", arg: modes };
  }
  if (kind === "terminal") {
    const cmd = terminalCommand(toolCall);
    return { verb: "Terminal", arg: cmd ?? shortTitle(toolCall.tool, "shell") };
  }
  if (kind === "edit") {
    const path =
      pickString(input, ["path", "file_path", "target_file", "filePath"]) ??
      diffPath(toolCall.contentBlocks) ??
      pathFromTitle(toolCall.tool);
    return { verb: bare === "Write" || bare === "write" ? "Write" : "Edit", arg: shortPath(path) };
  }
  if (kind === "read") {
    const path =
      pickString(input, ["path", "file_path", "target_file", "filePath"]) ??
      pathFromTitle(toolCall.tool);
    const range = lineRange(input);
    const arg = shortPath(path) + (range ? `:${range}` : "");
    return { verb: "Read", arg };
  }
  if (kind === "search") {
    const pattern =
      pickString(input, ["pattern", "query", "q"]) ??
      patternFromTitle(toolCall.tool) ??
      "";
    const scope =
      pickString(input, ["path", "glob", "target_directory"]) ?? "";
    const arg = [pattern ? `"${truncate(pattern, 48)}"` : "", scope ? shortPath(scope) : ""]
      .filter(Boolean)
      .join(" · ");
    return { verb: "Search", arg };
  }
  if (kind === "sql") {
    const q = typeof input.query === "string" ? input.query : "";
    const summary = q ? firstSqlClause(q) : bare;
    return { verb: "Query", arg: summary };
  }

  const known = TOOL_LABELS[bare];
  if (known) return { verb: known, arg: bare === toolCall.tool ? "" : bare };
  // Titles that already read as "Read foo" / long commands become the arg.
  if (toolCall.tool.includes(" ") || toolCall.tool.includes("/")) {
    return { verb: firstWord(toolCall.tool), arg: restWords(toolCall.tool) };
  }
  return { verb: toolCall.tool, arg: "" };
}

export function terminalCommand(toolCall: ToolCallStatus): string | null {
  const fromInput =
    typeof toolCall.input?.command === "string"
      ? (toolCall.input.command as string)
      : typeof toolCall.input?.cmd === "string"
        ? (toolCall.input.cmd as string)
        : null;
  if (toolCall.kind === "execute") {
    // When the title IS the command (some adapters), prefer input then title.
    if (fromInput) return fromInput;
    if (toolCall.tool && toolCall.tool !== "Terminal" && toolCall.tool !== "Bash") {
      return toolCall.tool;
    }
    return fromInput;
  }
  if (fromInput) return fromInput;
  if (toolCall.kind == null && /^[a-z0-9_./~-]+\s/.test(toolCall.tool)) {
    return toolCall.tool;
  }
  return null;
}

/** Extract plain text from contentBlocks / preview for terminal output. */
export function extractTextOutput(
  blocks: ToolContentBlock[] | null | undefined,
  preview?: string,
): string {
  if (blocks?.length) {
    const parts: string[] = [];
    for (const b of blocks) {
      if (b.type === "content" && typeof b.content?.text === "string") {
        parts.push(b.content.text);
      }
    }
    if (parts.length) return unwrapConsoleFence(parts.join(""));
  }
  if (!preview) return "";
  // preview is often a JSON-stringified content array
  try {
    const parsed = JSON.parse(preview);
    if (Array.isArray(parsed)) {
      const parts = parsed
        .map((b) =>
          b?.type === "content" && typeof b?.content?.text === "string"
            ? b.content.text
            : typeof b === "string"
              ? b
              : null,
        )
        .filter((x): x is string => Boolean(x));
      if (parts.length) return unwrapConsoleFence(parts.join(""));
    }
    if (typeof parsed === "string") return unwrapConsoleFence(parsed);
  } catch {
    // not JSON
  }
  return unwrapConsoleFence(preview);
}

function unwrapConsoleFence(text: string): string {
  const m = text.match(/^```(?:console|bash|shell|text)?\n([\s\S]*?)```\s*$/);
  return m ? m[1] : text;
}

export interface DiffHunkLine {
  type: "add" | "del" | "ctx" | "hunk";
  text: string;
  oldNo?: number;
  newNo?: number;
}

export interface DiffStat {
  additions: number;
  deletions: number;
}

export function diffPath(blocks: ToolContentBlock[] | null | undefined): string | null {
  const d = blocks?.find((b) => b.type === "diff");
  return typeof d?.path === "string" ? d.path : null;
}

export function extractDiff(
  toolCall: ToolCallStatus,
): { path: string | null; oldText: string; newText: string } | null {
  const block = toolCall.contentBlocks?.find((b) => b.type === "diff");
  if (block) {
    return {
      path: typeof block.path === "string" ? block.path : null,
      oldText: typeof block.oldText === "string" ? block.oldText : "",
      newText: typeof block.newText === "string" ? block.newText : "",
    };
  }
  // Fallback: search_replace / write inputs
  const input = toolCall.input ?? {};
  const oldText =
    typeof input.old_string === "string"
      ? (input.old_string as string)
      : typeof input.oldText === "string"
        ? (input.oldText as string)
        : "";
  const newText =
    typeof input.new_string === "string"
      ? (input.new_string as string)
      : typeof input.newText === "string"
        ? (input.newText as string)
        : typeof input.content === "string"
          ? (input.content as string)
          : "";
  if (!oldText && !newText) return null;
  return {
    path:
      pickString(input, ["path", "file_path", "target_file", "filePath"]) ?? null,
    oldText,
    newText,
  };
}

/**
 * Build a simple unified-style line list. Uses a common-prefix/suffix +
 * middle replace approach (good enough for the typical small ACP diffs);
 * falls back to all-red then all-green when no shared edges.
 * new-text-only (no oldText) → green-only rows, no red.
 */
export function buildDiffLines(oldText: string, newText: string): {
  lines: DiffHunkLine[];
  stat: DiffStat;
} {
  const oldLines = oldText === "" ? [] : oldText.replace(/\n$/, "").split("\n");
  const newLines = newText === "" ? [] : newText.replace(/\n$/, "").split("\n");

  if (oldLines.length === 0 && newLines.length === 0) {
    return { lines: [], stat: { additions: 0, deletions: 0 } };
  }
  if (oldLines.length === 0) {
    const lines: DiffHunkLine[] = newLines.map((text, i) => ({
      type: "add",
      text,
      newNo: i + 1,
    }));
    return { lines, stat: { additions: newLines.length, deletions: 0 } };
  }
  if (newLines.length === 0) {
    const lines: DiffHunkLine[] = oldLines.map((text, i) => ({
      type: "del",
      text,
      oldNo: i + 1,
    }));
    return { lines, stat: { additions: 0, deletions: oldLines.length } };
  }

  let prefix = 0;
  while (
    prefix < oldLines.length &&
    prefix < newLines.length &&
    oldLines[prefix] === newLines[prefix]
  ) {
    prefix++;
  }
  let suffix = 0;
  while (
    suffix < oldLines.length - prefix &&
    suffix < newLines.length - prefix &&
    oldLines[oldLines.length - 1 - suffix] === newLines[newLines.length - 1 - suffix]
  ) {
    suffix++;
  }

  const lines: DiffHunkLine[] = [];
  let oldNo = 1;
  let newNo = 1;
  const contextBefore = Math.min(3, prefix);
  const ctxStart = prefix - contextBefore;
  if (contextBefore > 0 || prefix + suffix < Math.max(oldLines.length, newLines.length)) {
    lines.push({
      type: "hunk",
      text: `@@ -${ctxStart + 1},${oldLines.length - ctxStart - suffix} +${ctxStart + 1},${newLines.length - ctxStart - suffix} @@`,
    });
  }
  for (let i = ctxStart; i < prefix; i++) {
    lines.push({ type: "ctx", text: oldLines[i], oldNo: oldNo + i, newNo: newNo + i });
  }
  oldNo = prefix + 1;
  newNo = prefix + 1;
  for (let i = prefix; i < oldLines.length - suffix; i++) {
    lines.push({ type: "del", text: oldLines[i], oldNo: oldNo++ });
  }
  for (let i = prefix; i < newLines.length - suffix; i++) {
    lines.push({ type: "add", text: newLines[i], newNo: newNo++ });
  }
  for (let i = 0; i < Math.min(3, suffix); i++) {
    const oi = oldLines.length - suffix + i;
    const ni = newLines.length - suffix + i;
    lines.push({
      type: "ctx",
      text: oldLines[oi],
      oldNo: oi + 1,
      newNo: ni + 1,
    });
  }

  return {
    lines,
    stat: {
      additions: Math.max(0, newLines.length - prefix - suffix),
      deletions: Math.max(0, oldLines.length - prefix - suffix),
    },
  };
}

export function languageFromPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const base = path.split(/[/\\]/).pop() ?? path;
  const dot = base.lastIndexOf(".");
  if (dot <= 0) return null;
  return base.slice(dot + 1).toLowerCase();
}

/** Parse search hit counts from a free-form preview when structured data is absent. */
export function parseSearchHits(
  preview: string | undefined,
  blocks: ToolContentBlock[] | null | undefined,
): { path: string; count: number }[] {
  const text = extractTextOutput(blocks, preview);
  if (!text) return [];
  const hits: { path: string; count: number }[] = [];
  // "path: N matches" / "path (N)" / ripgrep file headers
  for (const line of text.split("\n")) {
    const m =
      line.match(/^\s*([^\s].+?)\s*[:·]\s*(\d+)\s*matches?\s*$/i) ||
      line.match(/^\s*([^\s].+?)\s+\((\d+)\)\s*$/);
    if (m) hits.push({ path: m[1], count: Number(m[2]) });
  }
  return hits;
}

// ---- small string helpers ----

function pickString(obj: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v) return v;
  }
  return null;
}

function shortPath(path: string | null | undefined): string {
  if (!path) return "";
  // Prefer repo-relative when the path contains a known root marker.
  const markers = ["/hickory-ai3/", "/hickory-ai/", "/opt/dev/"];
  for (const m of markers) {
    const i = path.indexOf(m);
    if (i >= 0) return path.slice(i + m.length);
  }
  if (path.length > 56) {
    const parts = path.split("/");
    if (parts.length > 3) {
      return `…/${parts.slice(-2).join("/")}`;
    }
  }
  return path;
}

function pathFromTitle(title: string): string | null {
  // "Read path/to/file (1 - 20)" / "Edit relay/x.mjs" / "Read File"
  const m = title.match(/^(?:Read|Edit|Write|Search)\s+(.+?)(?:\s+\([\d\s–-]+\))?$/i);
  if (!m) return null;
  const rest = m[1].trim();
  if (rest.toLowerCase() === "file") return null;
  return rest;
}

function patternFromTitle(title: string): string | null {
  const m = title.match(/Search(?:\s+for)?\s+['"](.+?)['"]/i);
  return m ? m[1] : null;
}

function lineRange(input: Record<string, unknown>): string | null {
  const offset = typeof input.offset === "number" ? input.offset : null;
  const limit = typeof input.limit === "number" ? input.limit : null;
  if (offset != null && limit != null) return `${offset}–${offset + limit}`;
  if (offset != null) return `${offset}+`;
  return null;
}

function firstSqlClause(q: string): string {
  const one = q.replace(/\s+/g, " ").trim();
  return truncate(one, 56);
}

function firstWord(s: string): string {
  return s.trim().split(/\s+/)[0] ?? s;
}

function restWords(s: string): string {
  const i = s.trim().indexOf(" ");
  return i < 0 ? "" : s.trim().slice(i + 1);
}

function shortTitle(tool: string, fallback: string): string {
  if (!tool || tool === "Terminal" || tool === "Bash") return fallback;
  return truncate(tool, 64);
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
